import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

const OverlayQueueContext = createContext(null);

export const OverlayQueueProvider = ({ children }) => {
  const [registrations, setRegistrations] = useState({});

  const registerOverlay = useCallback((key, priority, isEligible) => {
    setRegistrations(prev => {
      const existing = prev[key];
      if (existing && existing.priority === priority && existing.isEligible === isEligible) {
        return prev;
      }
      return {
        ...prev,
        [key]: { priority, isEligible }
      };
    });
  }, []);

  const unregisterOverlay = useCallback((key) => {
    setRegistrations(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const activeOverlayKey = useMemo(() => {
    let winningKey = null;
    let highestPriority = Infinity;

    for (const [key, config] of Object.entries(registrations)) {
      if (config.isEligible && config.priority < highestPriority) {
        highestPriority = config.priority;
        winningKey = key;
      }
    }
    return winningKey;
  }, [registrations]);

  const value = useMemo(() => ({
    registerOverlay,
    unregisterOverlay,
    activeOverlayKey,
  }), [registerOverlay, unregisterOverlay, activeOverlayKey]);

  return (
    <OverlayQueueContext.Provider value={value}>
      {children}
    </OverlayQueueContext.Provider>
  );
};

export const useOverlayQueue = () => {
  const ctx = useContext(OverlayQueueContext);
  return ctx || { registerOverlay: () => {}, unregisterOverlay: () => {}, activeOverlayKey: null };
};

export const useRegisterOverlay = (key, priority, isEligible) => {
  const { registerOverlay, unregisterOverlay, activeOverlayKey } = useOverlayQueue();

  useEffect(() => {
    registerOverlay(key, priority, Boolean(isEligible));
  }, [key, priority, isEligible, registerOverlay]);

  useEffect(() => {
    return () => {
      unregisterOverlay(key);
    };
  }, [key, unregisterOverlay]);

  return {
    isActive: activeOverlayKey === key,
    activeOverlayKey,
  };
};

export default OverlayQueueContext;
